import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Chip,
  Divider,
  IconButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowCircleUp,
  ArrowsIn,
  ArrowsOut,
  Plus,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Document, Page } from 'react-pdf';

import { FilePickerTrigger, usePolicyForm } from './CompanyPolicyForm';
import PolicyCommentsWrapper from './PolicyComments';

import { Button } from '~/components/ui/button';
import { Card, CardOverflow } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FieldGroup, Fieldset, Label, Legend } from '~/components/ui/fieldset';
import { FilePicker } from '~/components/ui/file-picker';
import { Field } from '~/components/ui/form';
import { InputField } from '~/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import {
  HyperLink,
  EditorControls,
  FontSizeMenu,
  HeadingMenu,
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { SwitchField } from '~/components/ui/switch';
import { Tab, TabList, TabPanel, Tabs } from '~/components/ui/tabs';
import QueryResolver from '~/services/api/query-resolver';
import { TenantService } from '~/services/api/tenant/endpoints';
import { FAQObject, TabType } from '~/services/api/tenant/types';

export function PolicyTabField() {
  const intl = useIntl();
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');

  const [tabValue, setTabValue] = useState(
    form.watch('customTabs')[0]?.id.toString() ?? '',
  );

  const customTabs = useWatch({
    control: form.control,
    name: 'customTabs',
  });

  const tabBaseOptions = {
    shouldDisplayInOverview: true,
    is_new: true,
    is_deleted: false,
  };

  const addTabHandler = (tab: number, title: string) => {
    const id = -(customTabs.length + 1);
    form.setValue('customTabs', [
      ...customTabs,
      {
        id,
        title,
        type: tab,
        content: '',
        ...tabBaseOptions,
      },
    ]);
    setTabValue(id.toString());
  };

  const addFAQTabHandler = (tab: number, title: string) => {
    const id = -(customTabs.length + 1);
    const faqContent = [
      {
        question: '',
        answer: '',
      },
    ];

    form.setValue('customTabs', [
      ...customTabs,
      {
        id,
        title,
        type: tab,
        content: faqContent,
        ...tabBaseOptions,
      },
    ]);
    setTabValue(id.toString());
  };

  return (
    <Tabs
      value={tabValue}
      onChange={(_, value) => {
        if (value) {
          setTabValue(String(value));
        }
      }}
    >
      <TabList sx={{ overflowX: 'auto' }}>
        {customTabs.map((tab) => {
          if (tab.is_deleted) return null;
          return (
            <Tab
              key={tab.id}
              value={tab.id.toString()}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {tab.title}
            </Tab>
          );
        })}
        {isEditing &&
          form.watch('customTabs').filter((tab) => !tab.is_deleted).length <
            7 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  startDecorator={<Plus />}
                  variant='plain'
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {intl.formatMessage({ defaultMessage: 'Add New Section' })}
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className='flex flex-col'>
                  <Button
                    variant='plain'
                    color='neutral'
                    onClick={() => addTabHandler(TabType.REGULAR, 'New')}
                    className='flex !justify-start'
                  >
                    <FormattedMessage defaultMessage='New tab' />
                  </Button>
                  <Button
                    variant='plain'
                    color='neutral'
                    onClick={() => addTabHandler(TabType.VIDEO, 'Video')}
                    className='flex !justify-start'
                  >
                    <FormattedMessage defaultMessage='Video' />
                  </Button>
                  <Button
                    variant='plain'
                    color='neutral'
                    onClick={() => addTabHandler(TabType.PDF, 'PDF')}
                    className='flex !justify-start'
                  >
                    <FormattedMessage defaultMessage='PDF' />
                  </Button>
                  {/* hide add FAQ Button if there is a FAQ tab already*/}
                  {!form
                    .watch('customTabs')
                    .find(
                      (tab) => tab.type === TabType.FAQ && !tab.is_deleted,
                    ) && (
                    <Button
                      variant='plain'
                      color='neutral'
                      onClick={() => addFAQTabHandler(TabType.FAQ, 'FAQ')}
                      className='flex !justify-start'
                    >
                      <FormattedMessage defaultMessage='FAQ' />
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
      </TabList>
      {customTabs.map((customTab, index) => {
        if (customTab.is_deleted) return null;
        return (
          <TabPanel key={customTab.id} value={customTab.id.toString()}>
            <section className='space-y-4 py-4'>
              <Fieldset
                aria-label='New Custom Tab'
                className='flex flex-col gap-4'
              >
                <ProgramTabHeaderOption id={customTab.id} index={index} />
                {/* POLICY OR REGULAR TAB TYPE */}
                {[TabType.POLICY, TabType.REGULAR].includes(customTab.type) && (
                  <RegularTabContent id={customTab.id} index={index} />
                )}
                {/* INFOGRAPHICS TAB TYPE */}
                {customTab.type === TabType.INFOGRAPHIC && (
                  <InfographicsContent id={customTab.id} index={index} />
                )}
                {/* FAQ TAB TYPE */}
                {customTab.type === TabType.FAQ && (
                  <FAQContent id={customTab.id} index={index} />
                )}
                {/* PDF OR VIDEO TAB TYPE */}
                {[TabType.PDF, TabType.VIDEO].includes(customTab.type) && (
                  <VideoPDFContent id={customTab.id} index={index} />
                )}
              </Fieldset>
            </section>
          </TabPanel>
        );
      })}
    </Tabs>
  );
}

// HEADER OF ALL PROGRAM TABS
function ProgramTabHeaderOption({
  id,
  index,
}: Readonly<{ index: number; id: number }>) {
  const intl = useIntl();
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');
  const tab = form.watch('customTabs').find((item) => item.id === id);

  const onTabRemove = () => {
    form.setValue(`customTabs.${index}.is_deleted`, true);
    // Remove custom tabs that is both is_deleted and is_new to avoid unecessary validations
    const tabs = form.watch('customTabs');
    const updatedTabs = tabs
      .map((customTab, i) =>
        i === index ? { ...customTab, is_deleted: true } : customTab,
      )
      .filter((customTab) => !(customTab.is_new && customTab.is_deleted));
    form.setValue('customTabs', updatedTabs);
  };

  if (tab) {
    return (
      <>
        {isEditing ? (
          <div className='flex justify-between items-center mt-8'>
            <div className='flex items-end justify-between'>
              {tab?.type === TabType.FAQ && (
                <Button
                  variant='plain'
                  color='primary'
                  startDecorator={<Plus />}
                  onClick={() => {
                    const currentFaqs = tab.content ?? [];
                    const newFaq = { question: '', answer: '' };
                    form.setValue(`customTabs.${index}.content`, [
                      ...currentFaqs,
                      newFaq,
                    ]);
                  }}
                >
                  <FormattedMessage defaultMessage='Add Question' />
                </Button>
              )}
              {/* Title is hidden for FAQ and INFOGRAPHICS */}
              {tab?.type !== TabType.FAQ &&
                tab?.type !== TabType.INFOGRAPHIC && (
                  <InputField
                    label={intl.formatMessage({
                      defaultMessage: 'Section Title',
                    })}
                    control={form.control}
                    field={`customTabs.${index}.title` as const}
                  />
                )}
            </div>
            <div className='flex gap-2 items-center justify-end'>
              {![TabType.POLICY, TabType.INFOGRAPHIC].includes(tab?.type) && (
                <Button
                  startDecorator={<Trash />}
                  color='danger'
                  variant='plain'
                  onClick={onTabRemove}
                >
                  {intl.formatMessage({ defaultMessage: 'Remove Section' })}
                </Button>
              )}
              {/* 
                Display on program option is not available for POLICY
              */}
              {tab.type !== TabType.POLICY && (
                <SwitchField
                  control={form.control}
                  field={`customTabs.${index}.shouldDisplayInOverview` as const}
                  label={intl.formatMessage({
                    defaultMessage: 'Display on program overview',
                  })}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {tab.shouldDisplayInOverview && (
              <Chip color='secondary'>
                <FormattedMessage defaultMessage='Displayed in Overview' />
              </Chip>
            )}
          </>
        )}
      </>
    );
  }
  return <></>;
}

function RegularTabContent({
  id,
  index,
}: Readonly<{ index: number; id: number }>) {
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');
  const tab = form.watch('customTabs').find((item) => item.id === id);

  const enableComments = form.watch('enableComments');

  if (!tab || (tab?.type !== TabType.POLICY && tab?.type !== TabType.REGULAR)) {
    return null;
  }

  const setEnableComments = (value: boolean) => {
    form.setValue('enableComments', value);
  };

  return (
    <FieldGroup>
      {isEditing ? (
        <Field control={form.control} field={`customTabs.${index}.content`}>
          <RichTextField />
        </Field>
      ) : (
        <div className='h-full'>
          {tab.type === TabType.POLICY ? (
            <div className='h-full'>
              <PolicyCommentsWrapper
                policy={{
                  id: tab.id ?? 0,
                  content: tab.content,
                }}
                enableComments={enableComments}
                setEnableComments={() => setEnableComments(!enableComments)}
              >
                {(data) => {
                  return (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data.policyText.replace(
                          /attached-comment/g,
                          '',
                        ),
                      }}
                      style={{
                        display: data.commentsMode ? 'none' : 'block',
                        overflowY: 'auto',
                        height: 'auto',
                        color: '#878787',
                      }}
                      className='tiptap'
                    />
                  );
                }}
              </PolicyCommentsWrapper>
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: tab.content }} />
          )}
        </div>
      )}
    </FieldGroup>
  );
}

function InfographicsContent({
  id,
  index,
}: Readonly<{ index: number; id: number }>) {
  const intl = useIntl();
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');
  const tab = form.watch('customTabs').find((item) => item.id === id);

  const infographicPresetQuery =
    TenantService.endpoints.getInfographicPresets.useQuery();

  if (!tab || tab?.type !== TabType.INFOGRAPHIC) {
    return null;
  }

  return (
    <>
      {!isEditing ? (
        <div className='flex justify-center items-center'>
          {tab?.selectedPreset?.url ? (
            <img
              alt='selected infographic'
              src={tab?.selectedPreset.url ?? ''}
              className='w-3/4'
            />
          ) : (
            <img alt='selected infographic' src={tab?.content ?? ''} />
          )}
        </div>
      ) : (
        <div className='flex flex-col justify-between gap-2'>
          <Fieldset>
            <Legend>
              {intl.formatMessage({
                defaultMessage: 'Select one to display',
              })}
            </Legend>
            <div className='flex flex-row gap-2  items-center'>
              <QueryResolver query={infographicPresetQuery}>
                {(data) => (
                  <InfographicPresetsView
                    presets={data}
                    selectPreset={(presetUrl, presetId) => {
                      form.setValue(
                        `customTabs.${index}.selectedPreset.url`,
                        presetUrl,
                      );
                      form.setValue(
                        `customTabs.${index}.selectedPreset.id`,
                        presetId,
                      );
                      form.setValue(
                        `customTabs.${index}.asset.file`,
                        undefined,
                      );
                      form.setValue(`customTabs.${index}.asset.url`, undefined);
                    }}
                  />
                )}
              </QueryResolver>
              <Typography>
                <FormattedMessage defaultMessage='or' />
              </Typography>
              <div className='flex flex-col gap-4'>
                <div className='flex'>
                  <Typography>
                    <FormattedMessage
                      defaultMessage='Upload your image file here <em>(jpeg, jpg, png)</em>'
                      values={{
                        em: (chunks) => <em>{chunks}</em>,
                      }}
                    />
                  </Typography>
                  <Tooltip
                    title={intl.formatMessage({
                      defaultMessage:
                        'Image size must not exceed 4000 pixels wide and file size must not exceed 20MB',
                    })}
                    placement='top'
                  >
                    <div className='pointer'>
                      <WarningCircle weight='fill' />
                    </div>
                  </Tooltip>
                </div>
                <Field
                  control={form.control}
                  field={`customTabs.${index}.asset.file`}
                >
                  <FilePicker
                    accept='image/*'
                    onFileAccept={(details) => {
                      if (details.files.length === 0) return;
                      form.setValue(
                        `customTabs.${index}.asset.file`,
                        details.files[0],
                      );
                      form.setValue(
                        `customTabs.${index}.asset.url`,
                        URL.createObjectURL(details.files[0]),
                      );
                      form.setValue(`customTabs.${index}.selectedPreset`, null);
                    }}
                  >
                    <FilePickerTrigger>
                      {intl.formatMessage({
                        defaultMessage: 'Choose File',
                      })}
                    </FilePickerTrigger>
                  </FilePicker>
                </Field>
              </div>
            </div>
          </Fieldset>
          <div className='flex justify-center items-center'>
            {tab?.selectedPreset?.url && (
              <img
                alt='selected infographic'
                src={tab?.selectedPreset.url}
                className='w-3/4'
              />
            )}
            {tab?.asset?.url && (
              <img alt='selected infographic' src={tab.asset.url} />
            )}
            {!tab?.asset?.url && !tab?.selectedPreset?.url && (
              <img alt='selected infographic' src={tab.content} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function FAQContent({ id, index }: Readonly<{ index: number; id: number }>) {
  const intl = useIntl();
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');
  const tab = form.watch('customTabs').find((item) => item.id === id);

  if (!tab || tab?.type !== TabType.FAQ) {
    return null;
  }

  const updateFAQs = (key: number) => {
    const currentFaqs = tab.content ?? [];
    const updatedFaqs = currentFaqs.filter((_, i) => i !== key);
    form.setValue(`customTabs.${index}.content`, updatedFaqs);
  };

  return (
    <div>
      {!isEditing ? (
        <div className='flex flex-col gap-4'>
          <ViewAllFAQ title={tab.title} faqs={tab.content} />
        </div>
      ) : (
        <div className='flex flex-col gap-4'>
          {tab.content?.map((faq, key) => (
            <Card key={`${key}-${faq.id}`}>
              <div className='flex justify-end'>
                <Button
                  variant='plain'
                  color='danger'
                  onClick={() => updateFAQs(key)}
                >
                  <FormattedMessage defaultMessage='Remove FAQ question' />
                </Button>
              </div>
              <FieldGroup>
                <InputField
                  size='lg'
                  control={form.control}
                  field={`customTabs.${index}.content.${key}.question` as const}
                  label={`${intl.formatMessage({
                    defaultMessage: 'FAQ Question',
                  })} ${key + 1}`}
                />
                <Field
                  size='lg'
                  control={form.control}
                  field={`customTabs.${index}.content.${key}.answer` as const}
                >
                  <Label className='!mb-2'>
                    <FormattedMessage
                      defaultMessage='FAQ {number} Answer'
                      values={{
                        number: key + 1,
                      }}
                    />
                  </Label>
                  <RichTextField height={35} />
                </Field>
              </FieldGroup>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function VideoPDFContent({
  id,
  index,
}: Readonly<{ index: number; id: number }>) {
  const form = usePolicyForm();
  const isEditing = form.watch('isEditing');
  const tab = form.watch('customTabs').find((item) => item.id === id);

  if (!tab || (tab?.type !== TabType.VIDEO && tab?.type !== TabType.PDF)) {
    return null;
  }

  const acceptedFileTypes =
    tab.type === TabType.VIDEO
      ? 'video/mp4, video/quicktime'
      : 'application/pdf';

  return (
    <div className='flex flex-col'>
      <div className='flex flex-col gap-4'>
        {isEditing && (
          <div>
            <Typography>
              <FormattedMessage defaultMessage='Upload your video file here' />
            </Typography>
            <Field
              control={form.control}
              field={`customTabs.${index}.asset.file`}
            >
              <FilePicker
                accept={acceptedFileTypes}
                onFileAccept={(details) => {
                  if (details.files.length === 0) return;
                  form.setValue(`customTabs.${index}.asset`, {
                    file: details.files[0],
                    url: URL.createObjectURL(details.files[0]),
                  });
                }}
              >
                <FilePickerTrigger>
                  <Button
                    color='neutral'
                    variant='soft'
                    startDecorator={<ArrowCircleUp weight='fill' />}
                  >
                    <FormattedMessage defaultMessage='Choose File' />
                  </Button>
                </FilePickerTrigger>
              </FilePicker>
            </Field>
          </div>
        )}
        <div className='flex justify-center items-center'>
          {tab?.type === TabType.VIDEO ? (
            <video
              controls
              src={tab?.asset?.url ?? tab?.content}
              className='w-full'
            >
              <track kind='captions' src='' label='No captions available' />
            </video>
          ) : (
            <ViewPDF file={tab?.asset?.url ?? tab?.content} />
          )}
        </div>
      </div>
    </div>
  );
}

function InfographicPresetsView({
  presets,
  selectPreset,
}: Readonly<{
  presets: { [key: string]: string };
  selectPreset: (presetUrl: string, id: number) => void;
}>) {
  return (
    <div className='flex gap-1'>
      {Object.entries(presets).map(([key, value], index) => (
        <Button
          variant='soft'
          color='neutral'
          key={key}
          onClick={() => selectPreset(value, index + 1)}
        >
          <img alt={key} src={value} width='56px' />
        </Button>
      ))}
    </div>
  );
}

export function ViewAllFAQ({
  title,
  faqs,
}: Readonly<{
  title: string;
  faqs: FAQObject[];
}>) {
  const [expandedState, setExpandedState] = useState<Record<number, boolean>>(
    faqs.reduce(
      (acc, faq) => {
        acc[faq.id as number] = true;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  const handleExpandAll = () => {
    const newExpandedState = faqs.reduce(
      (acc, faq) => {
        acc[faq.id as number] = true;
        return acc;
      },
      {} as Record<number, boolean>,
    );
    setExpandedState(newExpandedState);
  };

  const handleCollapseAll = () => {
    const newExpandedState = faqs.reduce(
      (acc, faq) => {
        acc[faq.id as number] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    );
    setExpandedState(newExpandedState);
  };

  const toggleExpanded = (id: number) => {
    setExpandedState((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex justify-between'>
        <Typography level='title-lg'>{title}</Typography>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            color='neutral'
            onClick={handleCollapseAll}
          >
            <FormattedMessage defaultMessage='Collapse all' />
          </Button>
          <Button variant='outlined' color='neutral' onClick={handleExpandAll}>
            <FormattedMessage defaultMessage='Expand all' />
          </Button>
        </div>
      </div>
      <AccordionGroup transition='0.2s ease' className='gap-4'>
        {faqs?.map((faq, key) => (
          <Card key={`${faq.id}-${key}`} className='!pb-0'>
            <Accordion
              variant='plain'
              expanded={expandedState[faq.id as number]}
              onChange={() => toggleExpanded(faq.id as number)}
            >
              <CardOverflow className='!p-0'>
                <AccordionSummary className='!py-4 !px-6 hover:!bg-gray-100'>
                  {faq.question}
                </AccordionSummary>
              </CardOverflow>
              <AccordionDetails variant='plain'>
                <Divider />
                <div
                  dangerouslySetInnerHTML={{
                    __html: faq.answer,
                  }}
                  className='tiptap overflow-hidden'
                />
              </AccordionDetails>
            </Accordion>
          </Card>
        ))}
      </AccordionGroup>
    </div>
  );
}

export function ViewPDF({ file }: Readonly<{ file: string }>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numOfPages, setNumOfPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumOfPages(numPages);
    setPageNumber(1);
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className='w-full flex flex-col justify-center items-center'
    >
      <Document
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        error='Something went wrong'
      >
        <Page
          width={containerWidth}
          pageNumber={pageNumber}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          devicePixelRatio={2}
        />
      </Document>
      <div className='flex flex-row justify-center items-center gap-4'>
        <IconButton
          disabled={pageNumber <= 1}
          onClick={() => setPageNumber(pageNumber - 1)}
        >
          <ChevronLeft />
        </IconButton>
        <Typography>
          <FormattedMessage defaultMessage='Page' /> {pageNumber} / {numOfPages}
        </Typography>
        <IconButton
          disabled={pageNumber >= numOfPages}
          onClick={() => {
            if (pageNumber < numOfPages) setPageNumber(pageNumber + 1);
          }}
        >
          <ChevronRight />
        </IconButton>
      </div>
    </div>
  );
}

function RichTextField({ height = 80 }: Readonly<{ height?: number }>) {
  const intl = useIntl();

  return (
    <RichTextEditor>
      <Card size='sm' sx={{ height: (theme) => theme.spacing(height) }}>
        <CardOverflow bleed variant='soft' color='neutral'>
          <div className='flex justify-end p-1'>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  color='neutral'
                  variant='soft'
                  startDecorator={<ArrowsOut />}
                >
                  {intl.formatMessage({ defaultMessage: 'Expand' })}
                </Button>
              </DialogTrigger>
              <DialogContent bleed size='7xl'>
                <Card
                  className='!h-[75vh]'
                  size='sm'
                  sx={{ height: (theme) => theme.spacing(72) }}
                >
                  <CardOverflow bleed variant='soft' color='neutral'>
                    <DialogClose asChild>
                      <div className='flex justify-end p-1'>
                        <Button
                          variant='soft'
                          color='neutral'
                          startDecorator={<ArrowsIn />}
                        >
                          {intl.formatMessage({
                            defaultMessage: 'Dismiss',
                          })}
                        </Button>
                      </div>
                    </DialogClose>
                  </CardOverflow>
                  <StandardRichTextEditor />
                </Card>
              </DialogContent>
            </Dialog>
          </div>
        </CardOverflow>
        <StandardRichTextEditor />
      </Card>
    </RichTextEditor>
  );
}

function StandardRichTextEditor() {
  return (
    <RichTextEditor>
      <div className='h-full overflow-y-auto'>
        <RichTextEditorContent />
      </div>
      <Divider />
      <div className='flex items-center w-full gap-2'>
        <EditorControls.Formatting />
        <Divider orientation='vertical' />
        <HeadingMenu />
        <FontSizeMenu />
        <Divider orientation='vertical' />
        <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
          <EditorControls.Bold />
          <EditorControls.Underline />
          <EditorControls.Italic />
          <Divider orientation='vertical' />
          <EditorControls.UnorderedList />
          <EditorControls.OrderedList />
          <HyperLink />
        </ToggleButtonGroup>
      </div>
      <style>{`
        .attached-comment {
          border-bottom: none;
          background-color: transparent;
          cursor: auto;
        }
      `}</style>
    </RichTextEditor>
  );
}
